import { getMapBalance } from "../../game/constants/balance.js";

export const REWARD_DEFS = {
	bonusGold: {
		title: "Bonus Gold",
		desc: "A small supply drop for the current map.",
		cooldownMs: 180000,
	},
	emergencyLife: {
		title: "Emergency Life",
		desc: "Restore colony shields when the line is close to collapse.",
		cooldownMs: 600000,
	},
	scoreBoost: {
		title: "Score Multiplier",
		desc: "Gain 1.5x score from kills for 90 seconds.",
		cooldownMs: 600000,
		durationMs: 90000,
	},
	betweenWaveCache: {
		title: "Between-Wave Cache",
		desc: "Optional bonus while the lane is quiet.",
		cooldownMs: 0,
		perWave: true,
	},
};

const getClaim = (state, rewardId) => state?.adRewards?.claims?.[rewardId] || null;

export const canClaimReward = (state, rewardId, now = Date.now()) => {
	const def = REWARD_DEFS[rewardId];
	if (!state || !def) return { ok: false, reason: "Unknown reward." };

	const claim = getClaim(state, rewardId);
	if (claim?.lastClaimedAt && now - claim.lastClaimedAt < def.cooldownMs) {
		const remaining = Math.ceil((def.cooldownMs - (now - claim.lastClaimedAt)) / 1000);
		return { ok: false, reason: `Ready in ${remaining}s.` };
	}

	if (rewardId === "emergencyLife" && state.lives > 8) {
		return { ok: false, reason: "Emergency shields unlock at 8 lives or lower." };
	}

	if (rewardId === "scoreBoost" && state.adRewards?.activeScoreMultiplier && now < state.adRewards.activeScoreMultiplier.expiresAt) {
		return { ok: false, reason: "Score boost already active." };
	}

	if (rewardId === "betweenWaveCache") {
		if ((state.stats?.wavesCleared || 0) < 1) {
			return { ok: false, reason: "Unlocks after your first cleared wave." };
		}
		if (state.adRewards?.availableBetweenWaveCacheWave == null) {
			return { ok: false, reason: "Clear a wave to earn the next cache." };
		}
	}

	return { ok: true };
};

export const applyReward = (state, rewardId, now = Date.now()) => {
	const check = canClaimReward(state, rewardId, now);
	if (!check.ok) return check;

	state.adRewards = state.adRewards || { claims: {}, waveClaims: {}, activeScoreMultiplier: null };
	state.adRewards.claims = state.adRewards.claims || {};
	state.adRewards.waveClaims = state.adRewards.waveClaims || {};
	const mapBalance = getMapBalance({
		mapIdx: state.mapIdx,
		mapDef: state.mapDef,
	});

	let message = "";
	if (rewardId === "bonusGold") {
		const amount = Math.round((66 + state.wave * 4) * mapBalance.bonusGoldScale);
		state.gold += amount;
		message = `Supply drop delivered: +${amount} gold.`;
	}

	if (rewardId === "emergencyLife") {
		const amount = 3;
		state.lives = Math.min(25, state.lives + amount);
		message = `Emergency shields restored: +${amount} lives.`;
	}

	if (rewardId === "scoreBoost") {
		const def = REWARD_DEFS.scoreBoost;
		state.adRewards.activeScoreMultiplier = {
			multiplier: 1.5,
			expiresAt: now + def.durationMs,
		};
		message = "Score multiplier armed for 90 seconds.";
	}

	if (rewardId === "betweenWaveCache") {
		const amount = Math.round((48 + state.wave * 3) * mapBalance.cacheGoldScale);
		state.gold += amount;
		state.adRewards.availableBetweenWaveCacheWave = null;
		message = `Between-wave cache opened: +${amount} gold.`;
	}

	state.adRewards.claims[rewardId] = {
		lastClaimedAt: now,
		count: (state.adRewards.claims[rewardId]?.count || 0) + 1,
	};
	state.notifications.push({
		id: state.nextId++,
		message,
		tone: "success",
		createdAt: now,
	});
	state.soundCues = state.soundCues || [];
	state.soundCues.push({ id: state.nextId++, name: "reward", intensity: 1 });

	return { ok: true, message };
};
